<template>
  <div>
    <v-row no-gutters>
      <v-col>
        <v-text-field
          id="KeysEditModal_general_name"
          :label="$tc('name')"
          :hint="$tc('key_name_hint')"
          v-model="name"
          :error-messages="nameErrors"
          counter
          @input="$v.name.$touch()"
          @blur="$v.name.$touch()"
        />
        <content-area :items="items" class="px-1" />
        <template v-if="isTypeRSA">
          <strong>
            {{ $tc("public_exponent") }}
          </strong>
          <template v-if="pubExponent">
            <p>2<sup>16</sup> + 1 (hexadecimal: 010001)</p>
          </template>
          <template v-else>
            <p>3 (hexadecimal: 000011)</p>
          </template>
        </template>
        <v-switch
          color="green"
          :label="$tc('blocked')"
          v-model="blocked"
          :ripple="false"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import ContentArea from '@/components/ContentArea.vue'
const BITMASK_EXPORTABLE = 1
const BITMASK_TEMPORARY = 1 << 3
const BITMASK_RSA_PUB_EXP3 = 1 << 12

export default {
  components: {
    ContentArea
  },
  data () {
    return {
      kcv: '',
      cmac: ''
    }
  },
  props: {
    info: {
      type: Object,
      required: false,
      default: () => {}
    },
    currentName: {
      type: String,
      required: true
    }
  },
  methods: {
    getTranslatedStateString (state) {
      const stateName = this.$store.getters['keys/getSP80057State']({
        st: state
      })
      return this.$t(stateName)
    },
    isFlagActive (bitmask, int) {
      return int !== 0 && int !== 16384 && bitmask === 1
        ? this.$tc('yes')
        : this.$tc('no')
    },
    pubExponent (bitmask = BITMASK_RSA_PUB_EXP3, int = this.info.attr) {
      return int && bitmask === 1
    },
    getKeyTypeSize () {
      const algDetail = this.$store.getters['keys/getKeyType']({
        type: this.info.type
      })

      return algDetail.id
    },
    getKcvOrCmac () {
      const name = this.computedName
      const isKcvOrCmac = this.isKcvOrCmac

      if (name !== null && isKcvOrCmac) {
        const kcvBody = {
          type: 1,
          key: name
        }

        const cmacBody = {
          type: 2,
          key: name
        }

        this.$store.dispatch('hsm/getKcvOrCmac', kcvBody).then(res => {
          if (typeof res === 'string') {
            res = JSON.parse(res)
          }
          this.kcv = res?.cks
        }).catch(err => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })

        this.$store.dispatch('hsm/getKcvOrCmac', cmacBody).then(res => {
          if (typeof res === 'string') {
            res = JSON.parse(res)
          }
          this.cmac = res?.cks
        }).catch(err => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })
      } else {
        this.kcv = null
        this.cmac = null
      }
    }
  },
  computed: {
    computedName () {
      return this.currentName
    },
    name: {
      get () {
        return this.$store.state.keys.meta.name
      },
      set (val) {
        this.$store.commit('keys/SET_META_NAME', val)
      }
    },
    blocked: {
      get () {
        return this.$store.state.keys.blocked
      },
      set (val) {
        this.$store.commit('keys/SET_BLOCKED', val)
      }
    },
    items () {
      this.getKcvOrCmac()

      if (this.info) {
        const generalItems = [
          {
            title: this.$tc('type'),
            data: this.getKeyTypeSize()
          },
          {
            title: this.$tc('temporary'),
            data: this.isFlagActive(BITMASK_TEMPORARY, this.info.attr)
          },
          {
            title: this.$tc('exportable'),
            data: this.isFlagActive(BITMASK_EXPORTABLE, this.info.attr)
          },
          {
            title: this.$tc('kcv'),
            data: this.kcv
          },
          {
            title: this.$tc('cmac'),
            data: this.cmac
          }
        ]
        return generalItems
      }
      return []
    },
    isTypeRSA (typeInteger) {
      if (this.info) {
        const algDetail = this.$store.getters['keys/getKeyType']({
          type: this.info.type
        })
        return algDetail.type === 'RSA'
      } else {
        return false
      }
    },
    isKcvOrCmac () {
      if (this.info) {
        const algDetail = this.$store.getters['keys/getKeyType']({
          type: this.info.type
        })

        const type = algDetail.type
        return type === 'AES' || type === '3DES' || type === 'DES'
      } else {
        return false
      }
    },
    // ERRORS
    nameErrors () {
      const errors = []
      if (!this.$v.name.$dirty) return errors
      !this.$v.name.required && errors.push(this.$t('$validation.required'))
      !this.$v.name.minLength &&
        errors.push(
          this.$t('$validation.min_length', {
            num: this.$v.name.$params.minLength.min
          })
        )
      !this.$v.name.maxLength &&
        errors.push(
          this.$t('$validation.max_length', {
            num: this.$v.name.$params.maxLength.max
          })
        )
      !this.$v.name.keyExists && errors.push('Key exists')
      !this.$v.name.validChars &&
        errors.push(this.$t('$validation.invalid_key_name'))
      return errors
    },
    inputPolicy () {
      return this.$store.state.secPolicy
    }
  },
  validations () {
    const validation = {}

    const keyExists = value => {
      if (!value) return true
      const keys = this.$store.state.hsm.listObjects
      const find = keys.find(key => key.id === value)
      return find === undefined
    }

    const validChars = value => {
      if (!value) return true
      const numbeOfMatches = value.match(/^[A-Za-z0-9_]+$/g)
      if (!numbeOfMatches) return false
      return numbeOfMatches.length === 1
    }

    validation.name = {
      required,
      minLength: minLength(this.inputPolicy.key_name_min_len),
      maxLength: maxLength(this.inputPolicy.key_name_max_len),
      keyExists,
      validChars
    }
    return validation
  }
}
</script>

<style lang="scss" scoped>
.v-card {
  background-color: transparent;
}
</style>
